import { OrderByDirection } from 'firebase/firestore';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { QuotationFirestoreService } from './quotations-firestore.service';
import { IQuotation } from '@simplyfly/shared';
import { IFindCondition } from '../firestore/firestore.service';
import { FunctionsService } from '../functions/functions.service';

@Injectable({
  providedIn: 'root'
})
export class QuotationService {
  constructor(
    private quotationFirestoreService: QuotationFirestoreService,
    private functionsService: FunctionsService
  ) {}

  create(quotation: IQuotation): Promise<string> {
    return this.quotationFirestoreService.create(quotation);
  }

  update(quotation: IQuotation): Promise<string> {
    return this.quotationFirestoreService.update(quotation);
  }

  delete(id: string): Promise<void> {
    return this.quotationFirestoreService.delete(id);
  }

  getFromId(id: string): Observable<IQuotation> {
    return this.quotationFirestoreService.docValue$(id);
  }

  getAll(): Observable<IQuotation[]> {
    return this.quotationFirestoreService.collectionValues$();
  }

  getAllForEnquiry(enquiryId: string): Observable<IQuotation[]> {
    return this.quotationFirestoreService.collectionValues$(ref =>
      ref.where('enquiryId', '==', enquiryId).orderBy('created', 'desc')
    );
  }

  getAllForEnquiryOnce(enquiryId: string): Promise<IQuotation[]> {
    return firstValueFrom(
      this.quotationFirestoreService.collectionValues$(ref => ref.where('enquiryId', '==', enquiryId))
    );
  }

  getQuotationPdfDownloadUrl(quotationId: string): Promise<string | null> {
    return this.functionsService.callFunctionAsPromise('getDownloadUrlForQuotationPdf', {
      id: quotationId
    });
  }

  getQuotationPdfPreviewUrl(quotationId: string): Promise<string | null> {
    return this.functionsService.callFunctionAsPromise('getSignedUrlForQuotationPdf', {
      id: quotationId
    });
  }

  regenerateQuotationPdf(quotationId: string): Promise<string | null> {
    return this.functionsService.callFunctionAsPromise('regenerateQuotationPdf', {
      id: quotationId
    });
  }

  resetPagination(paginationName: string = 'list'): void {
    this.quotationFirestoreService.resetPaginate(paginationName);
  }

  getPaginatedResult(
    paginationName: string = 'list',
    conditions: IFindCondition[] = [],
    orderBy: {
      field: string;
      direction: OrderByDirection;
    } = {
      field: 'created',
      direction: 'desc'
    },
    itemsPerPage: number = 20,
    direction: 'prev' | 'next' = 'next'
  ): Observable<IQuotation[]> {
    return this.quotationFirestoreService.paginate(
      conditions,
      orderBy,
      itemsPerPage,
      paginationName,
      direction
    );
  }
}
